<template>
  <div>
    <DefaultModal
      ref="modal"
      :title="$t('external_key')"
      submit-button-text="generate_external_key"
      :hide-save-button="!!generatedKey"
      :save-button-disabled="submitting || !usersId"
      :close-button-disabled="submitting"
      @submit="generateKey"
      @modal-closed="reset"
    >
      <template v-slot:content>
        <p v-if="generatedKey">{{ $t('external_key_created') }}</p>
        <v-text-field
          v-model="expiresAt"
          :label="$t('external_key_expires_at')"
          :rules="expirationRules"
          :readonly="!!generatedKey"
          :disabled="submitting"
          type="text"
          placeholder="2027-01-01 18:00:00"
          maxlength="19"
          prepend-inner-icon="mdi-calendar"
          @click:prepend-inner="openCalendar"
          required
          outlined
          dense
        />
        <v-text-field
          :value="generatedKey"
          :label="$t('external_key')"
          autocomplete="off"
          readonly
          outlined
          dense
          hide-details
        />
        <v-alert v-if="errorMessage" type="error" class="mt-4" role="alert">
          {{ errorMessage }}
        </v-alert>
      </template>
    </DefaultModal>
    <v-dialog v-model="calendarVisible" max-width="290px">
      <v-date-picker
        :value="calendarDate"
        :locale="$i18n.locale"
        @input="selectExpirationDate"
      >
        <v-spacer />
        <v-btn text @click="calendarVisible = false">{{ $t('close') }}</v-btn>
      </v-date-picker>
    </v-dialog>
  </div>
</template>

<script>
import DefaultModal from '@/components/DefaultModal.vue'
import UserAPI from '@/services/api/core/user'

export default {
  name: 'UserExternalAccessKey',

  components: { DefaultModal },

  data() {
    return {
      usersId: null,
      expiresAt: '',
      calendarVisible: false,
      calendarDate: '',
      generatedKey: '',
      submitting: false,
      errorMessage: '',
      expirationRules: [
        value => !!value || this.$t('form_rules.mandatory'),
        value => !!this.parseExpiration(value) || this.$t('external_key_expiration_format'),
        value => {
          const date = this.parseExpiration(value)
          return (date && date.getTime() > Date.now()) || this.$t('external_key_expiration_future')
        }
      ]
    }
  },

  methods: {
    openModal(usersId) {
      if (this.submitting) return

      this.reset()
      this.usersId = usersId
      this.expiresAt = `${this.localDate()} 18:00:00`
      this.$refs.modal.openModal()
      this.$nextTick(() => this.$refs.modal.$refs.form.resetValidation())
    },

    closeModal() {
      if (this.submitting) return

      this.$refs.modal.closeModal()
      this.reset()
    },

    reset() {
      this.usersId = null
      this.expiresAt = ''
      this.calendarVisible = false
      this.calendarDate = ''
      this.generatedKey = ''
      this.errorMessage = ''
    },

    localDate() {
      const now = new Date()
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    },

    openCalendar() {
      if (this.submitting || this.generatedKey) return

      this.calendarDate = this.parseExpiration(this.expiresAt) ? this.expiresAt.split(' ')[0] : this.localDate()
      this.calendarVisible = true
    },

    selectExpirationDate(date) {
      if (this.submitting || this.generatedKey) return

      const time = this.expiresAt.split(' ')[1] || '18:00:00'
      this.expiresAt = `${date} ${time}`
      this.calendarVisible = false
    },

    parseExpiration(value) {
      if (!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]) ([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value)) return null

      const date = new Date(value.replace(' ', 'T'))
      return Number.isFinite(date.getTime()) && date.getDate() === Number(value.slice(8, 10)) ? date : null
    },

    createKey() {
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      const bytes = new Uint8Array(30)
      let key = ''

      while (key.length < 30) {
        window.crypto.getRandomValues(bytes)
        for (const byte of bytes) {
          // Reject values outside a multiple of 62 to keep characters equally likely.
          if (byte < 248) key += alphabet[byte % alphabet.length]
          if (key.length === 30) break
        }
      }

      return key
    },

    async generateKey() {
      if (this.submitting || this.generatedKey || !this.usersId || !this.$refs.modal.$refs.form.validate()) return

      this.submitting = true
      this.errorMessage = ''

      try {
        const key = this.createKey()
        await UserAPI.updateExternalAccess(this.usersId, {
          external_access_token: key,
          external_access_token_expires_at: this.parseExpiration(this.expiresAt).toISOString()
        })
        this.generatedKey = key
      } catch (error) {
        this.errorMessage = this.$t('external_key_generation_failed')
      } finally {
        this.submitting = false
        this.$refs.modal.setIsSubmitting(false)
      }
    }
  }
}
</script>

